import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import { membershipApi } from "../api";
import { useMember, useMembershipCategories } from "../hooks";
import { MembershipPageHeader } from "../components/MembershipPageHeader";
import type { MemberStatus, MembershipSource } from "../types";

// ── Primitive form components ─────────────────────────────────────────────────

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs font-black uppercase tracking-wide text-slate-500">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

const inputCls =
  "w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 disabled:bg-slate-50 disabled:text-slate-400";

const selectCls =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100";

// ── Form state type ───────────────────────────────────────────────────────────

interface EditForm {
  companyName: string;
  categoryId: number | "";
  membershipCode: string;
  source: MembershipSource | "";
  status: MemberStatus | "";
  certificateIssued: boolean;
  notes: string;
}

const EMPTY_FORM: EditForm = {
  companyName: "",
  categoryId: "",
  membershipCode: "",
  source: "",
  status: "",
  certificateIssued: false,
  notes: "",
};

const SOURCE_OPTIONS: { label: string; value: MembershipSource }[] = [
  { label: "Website", value: "WEBSITE" },
  { label: "Email", value: "EMAIL" },
  { label: "Phone Call", value: "PHONE_CALL" },
  { label: "Walk-in", value: "WALK_IN" },
  { label: "Referral", value: "REFERRAL" },
  { label: "Bulk Upload", value: "BULK_UPLOAD" },
  { label: "Existing Member Import", value: "EXISTING_MEMBER_IMPORT" },
  { label: "Trade Team", value: "TRADE_TEAM" },
  { label: "Other", value: "OTHER" },
];

const STATUS_OPTIONS: { label: string; value: MemberStatus }[] = [
  { label: "Prospect", value: "PROSPECT" },
  { label: "Pending Review", value: "PENDING_REVIEW" },
  { label: "Board Pending", value: "BOARD_PENDING" },
  { label: "Board Approved", value: "BOARD_APPROVED" },
  { label: "Board Rejected", value: "BOARD_REJECTED" },
  { label: "Payment Pending", value: "PAYMENT_PENDING" },
  { label: "Payment Confirmed", value: "PAYMENT_CONFIRMED" },
  { label: "ICT Pending", value: "ICT_PENDING" },
  { label: "ICT Completed", value: "ICT_COMPLETED" },
  { label: "Training Pending", value: "TRAINING_PENDING" },
  { label: "Training Completed", value: "TRAINING_COMPLETED" },
  { label: "Active Member", value: "ACTIVE_MEMBER" },
  { label: "Suspended", value: "SUSPENDED" },
  { label: "Inactive", value: "INACTIVE" },
];

// ── Page ──────────────────────────────────────────────────────────────────────

export default function MemberEditPage() {
  const { memberId } = useParams();
  const id = Number(memberId);
  const navigate = useNavigate();

  const memberQuery = useMember(id);
  const categoriesQuery = useMembershipCategories();

  const [form, setForm] = useState<EditForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const m = memberQuery.data;
    if (!m) return;
    setForm({
      companyName: m.companyName ?? "",
      categoryId: m.categoryId ?? "",
      membershipCode: m.membershipCode ?? "",
      source: m.source ?? "",
      status: m.status ?? "",
      certificateIssued: m.certificateIssued ?? false,
      notes: m.notes ?? "",
    });
  }, [memberQuery.data]);

  const set = <K extends keyof EditForm>(key: K, value: EditForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    if (!form.companyName.trim()) {
      setError("Company name is required.");
      return;
    }
    if (!form.categoryId) {
      setError("Category is required.");
      return;
    }
    setError("");
    setSaving(true);
    try {
      await membershipApi.updateMember(id, {
        companyName: form.companyName.trim(),
        categoryId: Number(form.categoryId),
        membershipCode: form.membershipCode || undefined,
        source: form.source || undefined,
        status: form.status || undefined,
        certificateIssued: form.certificateIssued,
        notes: form.notes || undefined,
      });
      navigate(`/hr/membership/members/${id}`);
    } catch {
      setError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (memberQuery.isLoading) {
    return <div className="p-6 text-sm text-slate-500">Loading member…</div>;
  }

  if (!memberQuery.data) {
    return <div className="p-6 text-sm text-red-600">Member not found.</div>;
  }

  const m = memberQuery.data;

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <MembershipPageHeader
        title={`Edit — ${m.companyName}`}
        description={`Membership Code: ${m.membershipCode ?? "Not assigned"} · Category: ${m.categoryName}`}
        actions={
          <button
            type="button"
            onClick={() => navigate(`/hr/membership/members/${id}`)}
            className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-700 hover:bg-slate-50"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Profile
          </button>
        }
      />

      <div className="mx-auto mt-6 max-w-3xl">
        {error && (
          <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-700">
            {error}
          </div>
        )}

        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-black text-slate-900">Member Details</h2>
          <p className="mt-1 text-sm text-slate-500">Required fields are marked *.</p>

          <div className="mt-6 grid gap-5 md:grid-cols-2">
            {/* Company Name */}
            <div className="md:col-span-2">
              <Field label="Company Name" required>
                <input
                  value={form.companyName}
                  onChange={(e) => set("companyName", e.target.value)}
                  placeholder="e.g. ACME Tea Producers Ltd"
                  className={inputCls}
                />
              </Field>
            </div>

            {/* Category */}
            <Field label="Membership Category" required>
              <select
                value={form.categoryId}
                onChange={(e) =>
                  set("categoryId", e.target.value === "" ? "" : Number(e.target.value))
                }
                className={selectCls}
              >
                <option value="">Select category…</option>
                {(categoriesQuery.data ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>

            {/* Membership Code */}
            <Field label="Membership Code">
              <input
                value={form.membershipCode}
                onChange={(e) => set("membershipCode", e.target.value)}
                placeholder="e.g. EATTA-2025-0042"
                className={inputCls}
              />
            </Field>

            {/* Source */}
            <Field label="Application Source">
              <select
                value={form.source}
                onChange={(e) => set("source", e.target.value as MembershipSource | "")}
                className={selectCls}
              >
                <option value="">Select source…</option>
                {SOURCE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>

            {/* Status */}
            <Field label="Membership Status">
              <select
                value={form.status}
                onChange={(e) => set("status", e.target.value as MemberStatus | "")}
                className={selectCls}
              >
                <option value="">Select status…</option>
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>

            {/* Certificate Issued */}
            <Field label="Certificate Issued">
              <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-slate-200 px-4 py-3 hover:bg-slate-50">
                <input
                  type="checkbox"
                  checked={form.certificateIssued}
                  onChange={(e) => set("certificateIssued", e.target.checked)}
                  className="h-4 w-4 rounded accent-emerald-700"
                />
                <span className="text-sm font-bold text-slate-700">
                  Certificate has been issued to this member
                </span>
              </label>
            </Field>

            {/* Notes */}
            <div className="md:col-span-2">
              <Field label="Internal Notes">
                <textarea
                  value={form.notes}
                  onChange={(e) => set("notes", e.target.value)}
                  rows={4}
                  placeholder="Any internal notes about this member…"
                  className={inputCls}
                />
              </Field>
            </div>
          </div>

          {/* Read-only audit fields */}
          <div className="mt-6 border-t border-slate-100 pt-5">
            <p className="mb-3 text-xs font-black uppercase tracking-wide text-slate-400">
              Workflow Audit (read-only)
            </p>
            <div className="grid gap-3 text-sm md:grid-cols-2 xl:grid-cols-3">
              {[
                { label: "Board Approval Date", value: m.boardApprovalDate },
                { label: "Board Decision By", value: m.boardDecisionBy },
                { label: "Board Notes", value: m.boardNotes },
                { label: "Payment Confirmed At", value: m.paymentConfirmedAt },
                { label: "Payment Confirmed By", value: m.paymentConfirmedBy },
                { label: "ICT Completed At", value: m.ictCompletedAt },
                { label: "ICT Completed By", value: m.ictCompletedBy },
                { label: "Training Completed At", value: m.trainingCompletedAt },
                { label: "Training Completed By", value: m.trainingCompletedBy },
                { label: "Active Date", value: m.activeDate },
                { label: "Certificate Issue Date", value: m.certificateIssueDate },
                { label: "Auction Access", value: m.auctionAccessAllowed ? "Allowed" : "Not allowed" },
              ].map(({ label, value }) => (
                <div
                  key={label}
                  className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3"
                >
                  <p className="text-xs font-black uppercase tracking-wide text-slate-400">
                    {label}
                  </p>
                  <p className="mt-0.5 font-bold text-slate-700">{value ?? "—"}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Save / Cancel */}
        <div className="mt-4 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate(`/hr/membership/members/${id}`)}
            className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-black text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-2xl bg-emerald-700 px-5 py-3 text-sm font-black text-white hover:bg-emerald-800 disabled:opacity-60"
          >
            <Save className="h-4 w-4" />
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
